// https://www.v9digital.com/insights/sitemap-xml-why-changefreq-priority-are-important/
import { Context } from '@nuxt/types'
import { LogSitemap, SitemapType } from '~/assets/ts/sitemap/utils'
import {
  embedSermonUrl,
  RemoveTrailingSlash,
  siteBroadcasterUrl,
  siteShareUrl,
} from '~/assets/ts/utils/urls'
import { VideoSitemapSermons } from '~/apiclient/apisitemap'
import { PlayerMediaType } from '~/assets/ts/enums'

export type SitemapChangeFrequency =
  | 'never'
  | 'yearly'
  | 'monthly'
  | 'weekly'
  | 'daily'
  | 'hourly'
  | 'always'
  | string

export interface SitemapUrl {
  url: string
  priority?: number
  updated?: Date
  changeFrequency?: SitemapChangeFrequency
  isIndex?: boolean
  video?: VideoSitemapSermons
}

function encodeForXml(str: string) {
  return (
    str
      .replaceAll(/&/g, '&amp;')
      // eslint-disable-next-line no-control-regex
      .replaceAll(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '')
  )
}

function formatUrl(url: string): string {
  return siteShareUrl(encodeForXml(RemoveTrailingSlash(url)))
}

const sitemapMinDate = new Date('2025-03-05')

/**
 * If an update date is provided, we specify the day the new sitemap system was
 * introduced as a minimum.
 */
function clampDate(date?: Date) {
  if (date && date < sitemapMinDate) return sitemapMinDate
  return date
}

function videoObj(video?: VideoSitemapSermons) {
  if (!video) return ''
  const [
    sermonID,
    _,
    title,
    desc,
    duration,
    publishTimestamp,
    broadcasterID,
    broadcasterDisplayName,
    thumbnailUrl,
    videoUrl,
    keywords,
  ] = video
  const playerUrl = formatUrl(embedSermonUrl(sermonID, PlayerMediaType.Video))
  const publishDate = new Date(publishTimestamp).toISOString()
  const broadcasterUrl = formatUrl(siteBroadcasterUrl(broadcasterID))
  const displayName = encodeForXml(broadcasterDisplayName)

  let tags = (keywords || '')
    .split(/\s+/)
    .filter((keyword) => !!keyword)
    .map((keyword) => {
      return `      <video:tag>${encodeForXml(keyword)}</video:tag>`
    })
  tags = [...new Set(tags)]

  return [
    `    <video:video>`,
    `      <video:thumbnail_loc>${thumbnailUrl}</video:thumbnail_loc>`,
    `      <video:title>${encodeForXml(title)}</video:title>`,
    `      <video:description>${encodeForXml(desc || '')}</video:description>`,
    `      <video:content_loc>${videoUrl}</video:content_loc>`,
    `      <video:player_loc>${playerUrl}</video:player_loc>`,
    `      <video:duration>${duration}</video:duration>`,
    `      <video:publication_date>${publishDate}</video:publication_date>`,
    `      <video:family_friendly>yes</video:family_friendly>`,
    `      <video:uploader info="${broadcasterUrl}">${displayName}</video:uploader>`,
    `      <video:live>no</video:live>`,
    ...tags,
    `    </video:video>`,
  ].join('\n')
}

export function RenderSitemapUrl(obj: SitemapUrl) {
  const type = obj.isIndex ? 'sitemap' : 'url'
  const d = clampDate(obj.updated)
  const p = obj.priority
  const f = obj.changeFrequency
  return [
    `  <${type}>`,
    `    <loc>${formatUrl(obj.url)}</loc>`,
    d ? `    <lastmod>${d.toISOString()}</lastmod>` : '',
    p ? `    <priority>${p}</priority>` : '',
    f ? `    <changefreq>${f}</changefreq>` : '',
    videoObj(obj.video),
    `  </${type}>`,
  ]
    .filter((str) => !!str)
    .join('\n')
}

export function RenderSitemap(
  ctx: Context,
  sitemap: SitemapType,
  urls: SitemapUrl[]
): string {
  const isIndex = sitemap === SitemapType.Index
  LogSitemap(ctx, sitemap, urls)
  urls = urls.map((url) => {
    return { ...url, isIndex }
  })
  const render = urls.map((url) => RenderSitemapUrl(url)).join('\n')
  const type = isIndex ? 'sitemapindex' : 'urlset'
  const videoType = [SitemapType.Videos].includes(sitemap)
    ? ' xmlns:video="http://www.google.com/schemas/sitemap-video/1.1"'
    : ''
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<${type} xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"${videoType}>`,
    render,
    `</${type}>`,
  ].join('\n')
}

function XmlResponse({ res, ssrContext }: Context, xml: string) {
  res.setHeader('Content-Type', 'application/xml')
  res.end(xml)
  if (!ssrContext) return
  ssrContext.redirected = true
}

type SitemapRenderer = (
  ctx: Context,
  locale: string,
  params: string[]
) => Promise<string> | string

export async function RenderXml(
  ctx: Context,
  renderer: SitemapRenderer,
  params: string[]
) {
  const locale = params.pop() as string
  const render = await renderer(ctx, locale, params)
  XmlResponse(ctx, render)
}

type SitemapIndexRenderer = (ctx: Context) => string
export function RenderIndexXml(ctx: Context, renderer: SitemapIndexRenderer) {
  XmlResponse(ctx, renderer(ctx))
}
